// רשימת שכבות: בחירה, שינוי שם, נעילה, הסתרה, סדר z ושכפול (עריכה ידנית של המסמך).
import { memo, useState } from 'react';
import type { BannerDoc } from '@bb/schema';
import { duplicateLayer, moveZ, updateLayer } from './docUtils';
import { ROLE_LABEL, TYPE_LABEL } from './types';

interface Props {
  doc: BannerDoc;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onChange: (doc: BannerDoc, key?: string) => void;
}

function LayersPanel({ doc, selectedId, onSelect, onChange }: Props) {
  const [renaming, setRenaming] = useState<string | null>(null);
  const [name, setName] = useState('');
  const layers = [...doc.layers].sort((a, b) => b.zIndex - a.zIndex);
  return (
    <section className="ed-panel" aria-labelledby="layers-h">
      <h3 id="layers-h" className="ed-h">שכבות <span className="muted small">({layers.length})</span></h3>
      <ul className="ly-list" role="list">
        {layers.map((l, i) => {
          const active = l.id === selectedId;
          return (
            <li key={l.id} className={`ly-row ${active ? 'active' : ''} ${!l.visible ? 'hidden' : ''}`}>
              {renaming === l.id ? (
                <form className="row" style={{ flex: 1, gap: 4 }} onSubmit={(e) => { e.preventDefault(); onChange(updateLayer(doc, l.id, (x) => { x.name = name.slice(0, 80) || x.name; })); setRenaming(null); }}>
                  <input aria-label="שם שכבה" value={name} onChange={(e) => setName(e.target.value)} autoFocus style={{ flex: 1, width: 80 }} maxLength={80} onKeyDown={(e) => { if (e.key === 'Escape') setRenaming(null); }} />
                  <button className="btn small" type="submit">שמור</button>
                </form>
              ) : (
                <button className="ly-name" aria-pressed={active} onClick={() => onSelect(l.id)} onDoubleClick={() => { setRenaming(l.id); setName(l.name); }} title={`${l.id} — לחיצה כפולה לשינוי שם`}>
                  <span className="ly-type" aria-hidden="true">{l.type === 'text' ? 'T' : l.type === 'image' ? '▣' : l.type === 'button' ? '◉' : '■'}</span>
                  <span className="ly-label">{l.name}</span>
                  <span className="muted small">{l.role ? ROLE_LABEL[l.role] : TYPE_LABEL[l.type]}</span>
                  {(l.type === 'text' || l.type === 'button') && l.content.locked && <span title="טקסט קבוע בקמפיין — AI לא יערוך" aria-label="טקסט קבוע">🔒</span>}
                </button>
              )}
              <div className="ly-actions">
                <button className="icon-btn" aria-label={`${l.visible ? 'הסתר' : 'הצג'} ${l.name}`} aria-pressed={!l.visible} title={l.visible ? 'הסתרה' : 'הצגה'} onClick={() => onChange(updateLayer(doc, l.id, (x) => { x.visible = !x.visible; }))}>{l.visible ? '👁' : '◌'}</button>
                <button className="icon-btn" aria-label={`${l.locked ? 'שחרר נעילה' : 'נעל'} ${l.name}`} aria-pressed={l.locked} title={l.locked ? 'שחרור נעילה' : 'נעילה (מונעת גרירה בקנבס)'} onClick={() => onChange(updateLayer(doc, l.id, (x) => { x.locked = !x.locked; }))}>{l.locked ? '🔒' : '🔓'}</button>
                <button className="icon-btn" aria-label={`הבא קדימה ${l.name}`} title="קדימה" disabled={i === 0} onClick={() => onChange(moveZ(doc, l.id, 1))}>▲</button>
                <button className="icon-btn" aria-label={`שלח אחורה ${l.name}`} title="אחורה" disabled={i === layers.length - 1} onClick={() => onChange(moveZ(doc, l.id, -1))}>▼</button>
                <button className="icon-btn" aria-label={`שכפל ${l.name}`} title="שכפול" disabled={doc.layers.length >= 60} onClick={() => { const r = duplicateLayer(doc, l.id); if (r) { onChange(r.doc); onSelect(r.newId); } }}>⧉</button>
                <button className="icon-btn" aria-label={`שנה שם ${l.name}`} title="שינוי שם" onClick={() => { setRenaming(l.id); setName(l.name); }}>✎</button>
              </div>
            </li>
          );
        })}
      </ul>
      <button className="btn small" style={{ marginTop: 6 }} onClick={() => onSelect(null)} aria-pressed={selectedId === null}>הגדרות באנר (רקע)</button>
    </section>
  );
}
export default memo(LayersPanel);
